import type { GetCvtDiagnosticDetailsResponse, SbrResponseDataResponse } from '@packages/clients';

import type { CvtDiagnosticEntity, SbrDiagnosticEntity } from '../entities';
import type { Symptom } from './types';

import { bulletListToArray, normalizeMessage } from './transformers';

/**
 * Map the GetCvtDiagnosticDetailsResponse DTO retrieved from the backend to the CvtDiagnosticEntity used in the frontend,
 * including mapping the nested SBR response data to the SbrDiagnosticEntity.
 *
 * @param dto - The GetCvtDiagnosticDetailsResponse DTO retrieved from the backend.
 * @returns The CvtDiagnosticEntity used in the frontend.
 */
export function toCvtDiagnosticEntity(dto: GetCvtDiagnosticDetailsResponse): CvtDiagnosticEntity {
    return {
        accountId: dto.accountId,
        accountName: dto.accountName,
        diagnosticId: dto.diagnosticId,
        fileName: dto.fileName,
        jobCompletedTimestamp: dto.jobCompletedTimestamp,
        jobSubmittedTimestamp: dto.jobSubmittedTimestamp,
        roNumber: dto.roNumber,
        retailer: {
            name: dto.retailer.name,
            code: dto.retailer.code,
            district: dto.retailer.district,
            region: dto.retailer.region,
            zone: dto.retailer.zone,
            role: dto.retailer.role,
        },
        sbrData: toSbrData(dto.sbrResponseData),
        sbrDiagnosticId: dto.sbrDiagnosticId,
        sbrResponseDataUrl: dto.sbrResponseDataUrl,
        vin: dto.vin,
    };
}

/**
 * Map the SBR response data included in the GetCvtDiagnosticDetailsResponse DTO to the SbrDiagnosticEntity.
 * Excludes the graph data, which can be retrieved via the sbrResponseDataUrl.
 *
 * @param dto - The SBR response data included in the GetCvtDiagnosticDetailsResponse DTO.
 * @returns The SbrDiagnosticEntity used in the frontend.
 */
function toSbrData(dto: SbrResponseDataResponse | undefined): SbrDiagnosticEntity | undefined {
    if (!dto) {
        return undefined;
    }

    return {
        errorDetail: toSbrErrorDetail(dto.errorDetail),
        errorType: dto.errorType,
        repairRecommendation: bulletListToArray(dto.repairRecommendation),
        sbrDiagnosticId: dto.sbrDiagnosticId,
        symptoms: toSbrSymptoms(dto.symptomTitle, dto.symptomDescription),
    };
}

/**
 * Maps the error detail string included in the SBR response data to a normalized error detail string with consistent formatting.
 *
 * @param errorDetail - The error detail string included in the SBR response data, which may contain multiple lines and bullet points.
 * @returns A normalized error detail string with consistent formatting, or undefined if the input is not provided.
 */
function toSbrErrorDetail(errorDetail?: SbrResponseDataResponse['errorDetail']): string | undefined {
    if (!errorDetail) {
        return undefined;
    }
    return normalizeMessage(errorDetail);
}

/**
 *
 * Maps the symptom titles and descriptions included in the SBR response data to an array of Symptom objects.
 *
 * @param symptomTitle - The symptom titles included in the SBR response data.
 * @param symptomDescription - The symptom descriptions included in the SBR response data.
 * @returns  An array of Symptom objects or an empty array if the symptom titles or descriptions are not in the expected format.
 */
function toSbrSymptoms(
    symptomTitle: SbrResponseDataResponse['symptomTitle'],
    symptomDescription: SbrResponseDataResponse['symptomDescription']
): Symptom[] {
    if (!Array.isArray(symptomTitle) || !Array.isArray(symptomDescription) || symptomTitle.length === 0) {
        return [];
    }

    return symptomTitle.map((title, index) => ({
        title,
        description: symptomDescription?.[index] ?? '',
    }));
}
